import type { QueryClient } from "@tanstack/react-query";
import { useMutation } from "@connectrpc/connect-query";
import { Link, Outlet, createRootRouteWithContext } from "@tanstack/react-router";

import { AuthService } from "@yatayat/gen/yatayat/auth/v1/auth_pb";

import { SiteFooter } from "~/components/SiteFooter";
import { SmsOutbox } from "~/components/SmsOutbox";
import { useI18n } from "~/lib/i18n";
import { useRefreshSession, useSession } from "~/lib/session";
import { can, useStaffSession } from "~/lib/staff";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const navLink = "rounded-md px-2 py-1 text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900";
const navLinkActive = { className: "font-semibold text-ink dark:text-ink-dark" };

function RootLayout() {
  const { t, lang, setLang } = useI18n();

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-slate-200 dark:border-slate-800">
        <div aria-hidden="true" className="h-1 bg-ink dark:bg-ink-dark" />
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
          {/* Grows from zero, so on narrow phones the tag wraps under the name rather than pushing the language button down. */}
          <div className="min-w-0 flex-1 leading-tight">
            <Link to="/" lang="ne" className="text-lg font-semibold text-brand sm:text-xl dark:text-brand-dark">
              {t("siteNameNe")}
            </Link>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span lang="en" className="text-xs font-medium text-slate-600 dark:text-slate-400">
                {t("siteNameEn")}
              </span>
              <span className="rounded border border-amber-300 bg-amber-50 px-1.5 text-[11px] font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t("proofOfConcept")}
              </span>
            </div>
          </div>
          <nav aria-label={t("mainNav")} className="order-last flex w-full flex-wrap items-center gap-1 text-sm sm:order-none sm:w-auto">
            <Link to="/book" className={navLink} activeProps={navLinkActive}>
              {t("navBook")}
            </Link>
            <Link to="/tickets" className={navLink} activeProps={navLinkActive}>
              {t("navTickets")}
            </Link>
            <OperatorNavLink />
            <ConsoleNavLink />
            <SignOutButton />
          </nav>
          <button
            type="button"
            onClick={() => setLang(lang === "ne" ? "en" : "ne")}
            className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
          >
            {t("switchLanguage")}
          </button>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 pt-6">
        <Outlet />
      </main>
      <SiteFooter />
      <SmsOutbox />
      <DemoWatermark />
    </div>
  );
}

/** Shown only to signed-in operator staff; visitors aren't pointed at the operator app. */
function OperatorNavLink() {
  const { t } = useI18n();
  const role = useStaffSession().session?.role;

  if (role === undefined || !can.useOperatorApp(role)) return null;

  return (
    <Link to="/operator" className={navLink} activeProps={navLinkActive}>
      {t("navOperator")}
    </Link>
  );
}

/** Shown only to signed-in regulator staff. */
function ConsoleNavLink() {
  const { t } = useI18n();
  const role = useStaffSession().session?.role;

  if (role === undefined || !can.useConsole(role)) return null;

  return (
    <Link to="/console" className={navLink} activeProps={navLinkActive}>
      {t("consoleNav")}
    </Link>
  );
}

function SignOutButton() {
  const { t } = useI18n();
  const session = useSession();
  const refreshSession = useRefreshSession();
  const signOut = useMutation(AuthService.method.signOut, { onSuccess: () => refreshSession() });

  if (!session.data?.session) return null;

  return (
    <button type="button" disabled={signOut.isPending} onClick={() => signOut.mutate({})} className={`${navLink} disabled:opacity-60`}>
      {t("signOut")}
    </button>
  );
}

/** Always-visible marker that the data on screen is synthetic (ADR-0018). */
function DemoWatermark() {
  const { t } = useI18n();

  return (
    <div
      role="note"
      className="pointer-events-none fixed bottom-3 right-3 rounded-full bg-amber-400/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-950 shadow"
    >
      {t("demoData")}
    </div>
  );
}

function NotFound() {
  const { t } = useI18n();

  return <p className="text-slate-600 dark:text-slate-400">{t("notFound")}</p>;
}
